
export const ORB_VERTEX = /* glsl */ `
attribute vec2 position;
attribute vec2 uv;
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

// OCTAVES: 4 on desktop, 3 on small / slow devices.
export const orbFragment = (octaves: number): string => /* glsl */ `
precision highp float;
#define OCTAVES ${octaves}

varying vec2 vUv;
uniform float uTime;
uniform float uScale;
uniform float uTurb;
uniform float uFlow;
uniform float uGlow;
uniform float uRim;
uniform float uBright;
uniform float uAudio;
uniform float uHue;
uniform vec3 uBands;

// The GlowCard aurora palette (--aurora-1/2/3), on a deep indigo body.
const vec3 AURORA_A = vec3(0.753, 0.518, 0.988); // #c084fc purple
const vec3 AURORA_B = vec3(0.957, 0.447, 0.714); // #f472b6 pink
const vec3 AURORA_C = vec3(0.220, 0.741, 0.973); // #38bdf8 sky
const vec3 INK = vec3(0.035, 0.020, 0.110);

// Rotates a colour around the grey axis: the whole palette changes theme as uHue moves.
vec3 hueShift(vec3 c, float a) {
  const vec3 k = vec3(0.57735);
  float ca = cos(a);
  float sa = sin(a);
  return c * ca + cross(k, c) * sa + k * dot(k, c) * (1.0 - ca);
}

// Simplex noise (Ashima Arts / Ian McEwan, MIT).
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
          + i.y + vec4(0.0, i1.y, i2.y, 1.0))
          + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}

float fbm(vec3 p) {
  float a = 0.5;
  float s = 0.0;
  for (int i = 0; i < OCTAVES; i++) {
    s += a * snoise(p);
    p = p * 2.03 + 17.1;
    a *= 0.46;
  }
  return s;
}

void main() {
  vec2 p = vUv * 2.0 - 1.0;
  float r = length(p);
  float ang = atan(p.y, p.x);
  float t = uTime * uFlow;

  // Organic silhouette: slow lobes, finer ripples, and a voice-driven wobble.
  vec3 dir = vec3(cos(ang), sin(ang), 0.0);
  float def = snoise(dir * 0.9 + vec3(0.0, 0.0, t * 0.22)) * (0.055 + 0.050 * uTurb)
            + snoise(dir * 1.9 + vec3(5.0, 3.0, t * 0.35)) * (0.016 + 0.040 * uTurb)
            + snoise(dir * 3.3 + vec3(9.0, 1.0, t * 0.90)) * uAudio * 0.045;
  float R = 0.64 * uScale * (1.0 + def);

  float d = r - R;
  float rr = clamp(r / R, 0.0, 1.0);
  float z = sqrt(1.0 - rr * rr);
  vec3 n = normalize(vec3(p / R, z));

  // Liquid interior: domain-warped noise on the sphere surface.
  vec3 q = n * 0.8;
  q.z += t * 0.10;
  vec3 w = vec3(
    fbm(q * 0.9 + vec3(0.0, t * 0.14, 0.0)),
    fbm(q * 0.9 + vec3(7.1, 3.3, t * 0.12)),
    fbm(q * 0.8 + vec3(2.0, 9.0, 0.0))
  );
  float warp = 0.9 + 0.7 * uTurb;
  float f = fbm(q * 1.0 + w * warp);
  float f2 = fbm(q * 1.5 - w * 0.9 + t * 0.08);

  // The palette follows the voice: low, mid and high parts of the speech each brighten one colour,
  // and the whole set rotates with uHue.
  vec3 A = max(hueShift(AURORA_A, uHue), 0.0);
  vec3 B = max(hueShift(AURORA_B, uHue), 0.0);
  vec3 C = max(hueShift(AURORA_C, uHue), 0.0);
  float wA = 0.75 + 0.9 * uBands.y;
  float wB = 0.55 + 1.1 * uBands.x;
  float wC = 0.70 + 1.0 * uBands.z;

  // Dark indigo body, with purple and sky clouds and pink pooling lower-left.
  vec3 col = INK;
  col = mix(col, A * 0.42 * wA, smoothstep(0.0, 0.55, f));
  col = mix(col, C * 0.40 * wC, smoothstep(0.15, 0.65, f2) * 0.55);
  float lowerLeft = smoothstep(0.95, -0.15, length(n.xy - vec2(-0.45, -0.5)));
  col += B * lowerLeft * 0.42 * wB * (0.55 + 0.45 * smoothstep(-0.3, 0.5, f));

  // Thin bright threads where the noise crosses zero: the light moving inside the glass.
  float edge0 = 1.0 - z;
  float thread = exp(-pow(f * 4.2, 2.0));
  col += mix(C, A, f2 * 0.5 + 0.5) * thread * (0.18 + edge0 * 1.0) * (0.55 + uBright * 0.5 + uAudio * 0.6);

  // Edge: an iridescent rim that passes through all three colours around the circumference.
  float edge1 = 1.0 - z;
  vec3 rimCol = mix(mix(A, B, 0.5 + 0.5 * sin(ang + uTime * 0.6 * uFlow)), C, 0.5 + 0.5 * sin(ang * 2.0 - uTime * 0.4 * uFlow));
  col += rimCol * pow(edge1, 3.0) * 1.0 * uRim;
  col += vec3(1.0, 0.93, 1.0) * pow(edge1, 9.0) * 1.0 * uRim;

  // Soft specular from the upper left.
  vec3 L = normalize(vec3(-0.5, 0.65, 0.6));
  col += vec3(0.95, 0.85, 1.0) * pow(max(dot(n, L), 0.0), 38.0) * 0.30;

  col *= uBright;

  // Silhouette and the bloom around it.
  float body = smoothstep(0.012, -0.012, d);
  float outside = max(d, 0.0);
  float g = (exp(-outside * 7.5) * 0.55 + exp(-outside * 2.6) * 0.20) * uGlow * 0.3; // outer glow only, at reduced strength
  g *= smoothstep(1.0, 0.72, r); // reach zero before the canvas edge, so no box shows
  vec3 glowCol = mix(mix(A, B, 0.5 + 0.5 * sin(ang + uTime * 0.5 * uFlow)), C, 0.5 + 0.5 * sin(ang * 2.0 - uTime * 0.35 * uFlow)) * g;

  vec3 rgb = mix(glowCol, col, body);
  float alpha = clamp(max(body, g), 0.0, 1.0);

  // Light dithering, so the dark gradients do not band.
  float dither = (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
  gl_FragColor = vec4(max(rgb + dither, 0.0), alpha);
}
`;
